import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Plus } from 'lucide-react'
import { useAppSelector } from '@/hooks/useAppSelector'
import { useAppDispatch } from '@/hooks/useAppDispatch'
import { selectTenant, selectTenants, switchTenant } from '@/features/auth/authSlice'
import { selectIsImpersonating } from '@/features/auth/impersonation'
import {
  fetchOrganizationSettings,
  selectOrganizationSettings,
} from '@/features/settings/settingsSlice'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { SearchInput } from '@/components/ui/search-input'
import TenantBrandIdentity from '@/components/branding/TenantBrandIdentity'
import { useTheme } from '@/theme/useTheme'
import { cn } from '@/lib/utils'
import { assistAnchor } from '@/features/assist/anchors'
import { openWorkspace, workspaceHostTarget } from '@/utils/tenantSwitch'

// 40px, so it sits centered on the 8pt grid inside the topbar-height brand
// row (8 + 40 + 8 = 56 = --size-topbar) - same control height as every
// other primitive default (F-7 addendum polish).
const BRAND_TRIGGER_CLASSES =
  'flex min-h-10 min-w-0 items-center gap-2 rounded-control text-navigation-ink transition-colors duration-150 ease-out ' +
  'hover:bg-navigation-active/60 hover:text-navigation-active-ink ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navigation-ink'

/**
 * Tenant identity + switcher for the top of the primary navigation (desktop
 * sidebar rail and mobile drawer). This is the one place the current
 * tenant's logo/name is shown - it used to be duplicated with a second,
 * separate trigger on the right side of the header, which has been removed.
 *
 * @param {Object} props
 * @param {boolean} [props.collapsed=false] - Desktop sidebar rail collapsed
 *   to icons-only: shows just the logo/initials, with a tooltip carrying the
 *   name. The mobile drawer never passes this (always full width).
 * @returns {JSX.Element}
 */
export default function TenantSwitcher({ collapsed = false, variant = 'rail' }) {
  const isChip = variant === 'chip'
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentTenant = useAppSelector(selectTenant)
  const tenants = useAppSelector(selectTenants)
  const user = useAppSelector(state => state.auth.user)
  const organizationSettings = useAppSelector(selectOrganizationSettings)
  const isImpersonating = useAppSelector(selectIsImpersonating)
  const { branding } = useTheme()

  const [searchValue, setSearchValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // The logo lives on the settings slice, which only the settings pages fetch.
  // The sidebar renders on every route, so fetch it here too. Skipped once the
  // slice is already populated to avoid a redundant request on each mount.
  useEffect(() => {
    if (!organizationSettings) {
      dispatch(fetchOrganizationSettings())
    }
  }, [dispatch, organizationSettings])

  // Determine if search should be shown
  const showSearch = tenants.length > 5 || user?.is_staff === true

  // Filter tenants based on search
  const filteredTenants = searchValue
    ? tenants.filter(tenant =>
        tenant.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : tenants

  // Handle tenant switch
  const handleTenantSelect = async (tenant) => {
    if (tenant.id === currentTenant?.id) return

    try {
      // On a workspace host: the other workspace's own host. Otherwise the
      // backend switch, then a load of the new organization's section list -
      // not the current URL, which may name the old one's dashboard or metric.
      await openWorkspace(tenant, (target) => dispatch(switchTenant(target)).unwrap())
    } catch (error) {
      console.error('Failed to switch tenant:', error)
    }
  }

  // Clear search when dropdown closes
  const handleOpenChange = (open) => {
    setIsOpen(open)
    if (!open) {
      setSearchValue('')
    }
  }

  // Go create a new organization (the switcher is the entry point for users who
  // already belong to at least one org; the empty-state lives on /tenant-select).
  const handleCreateOrganization = () => {
    setIsOpen(false)
    navigate('/organizations/new')
  }

  // A tenant not yet resolved (e.g. mid-login) still gets the brand mark so
  // the sidebar/drawer header doesn't flash empty; it just isn't a switcher.
  if (!currentTenant) {
    return (
      <div
        className={cn(
          'flex min-h-10 min-w-0 items-center gap-2 rounded-control text-navigation-muted',
          collapsed ? 'w-11 justify-center px-0' : 'w-full flex-1 px-2'
        )}
      >
        <TenantBrandIdentity compact markClassName="h-9 w-9 shrink-0" surface="navigation" />
        {!collapsed && <span className="truncate text-sm">No tenant selected</span>}
      </div>
    )
  }

  const hasMultipleTenants = tenants.length > 1
  const displayName = String(branding.tenant_id) === String(currentTenant.id)
    ? branding.application_name
    : currentTenant.name

  // Impersonating: the backend pins the session to the target's tenant (which
  // may not be one of the admin's own), so show it as current but offer no
  // switching until the impersonation is exited.
  if (isImpersonating) {
    return (
      <div
        className={cn(
          'flex min-h-10 min-w-0 items-center gap-2 rounded-control text-navigation-ink',
          collapsed ? 'w-11 justify-center px-0' : 'w-full flex-1 px-2'
        )}
        data-testid="tenant-identity-pinned"
      >
        <TenantBrandIdentity compact markClassName="h-9 w-9 shrink-0" surface="navigation" />
        <span className={collapsed ? 'sr-only' : 'min-w-0 flex-1 truncate text-left text-sm font-semibold'}>
          {displayName}
        </span>
      </div>
    )
  }

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Switch organization"
        {...assistAnchor('nav.tenant_switcher')}
        data-tenant-chip={isChip ? '' : undefined}
        className={isChip
          ? 'flex h-9 min-w-0 max-w-[16rem] items-center gap-2 rounded-[6px] pl-1.5 pr-2.5 text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
          : cn(BRAND_TRIGGER_CLASSES, collapsed ? 'w-11 justify-center px-0' : 'w-full flex-1 px-2')}
      >
        <TenantBrandIdentity compact markClassName={isChip ? 'h-6 w-6 shrink-0 rounded-[5px]' : 'h-9 w-9 shrink-0'} surface="navigation" />
        {!collapsed && (
          <>
            <span data-chip-label={isChip ? '' : undefined} className={cn('min-w-0 flex-1 truncate text-left font-semibold', isChip ? 'text-label' : 'text-sm')}>
              {displayName}
            </span>
            <ChevronDown data-chip-label={isChip ? '' : undefined} className={cn('h-4 w-4 shrink-0', isChip ? 'text-ink-2' : 'text-navigation-muted')} aria-hidden="true" />
          </>
        )}
      </button>
    </DropdownMenuTrigger>
  )

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">{displayName}</TooltipContent>
        </Tooltip>
      ) : trigger}

      <DropdownMenuContent align={isChip ? 'end' : 'start'} className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-[280px] max-w-[calc(100vw-2rem)] overflow-y-auto">
        {/* Search input */}
        {showSearch && (
          <>
            <div className="p-2">
              <SearchInput
                aria-label="Search organizations"
                placeholder="Search organizations…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
              />
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Tenant list (only when there's more than one to choose from) */}
        {hasMultipleTenants && (
          <div className="max-h-[300px] overflow-y-auto">
            {filteredTenants.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-2">
                No organizations found
              </div>
            ) : (
              filteredTenants.map((tenant) => {
                const isCurrent = tenant.id === currentTenant?.id
                const hostTarget = isCurrent ? null : workspaceHostTarget(tenant)
                return (
                  <DropdownMenuItem
                    key={tenant.id}
                    onClick={() => handleTenantSelect(tenant)}
                    className="flex min-h-11 cursor-pointer items-center justify-between gap-3"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{tenant.name}</span>
                      {hostTarget && (
                        <span className="truncate text-xs text-ink-2">opens {hostTarget.address}</span>
                      )}
                    </span>
                    {isCurrent && (
                      <Check className="w-4 h-4 text-brand" />
                    )}
                  </DropdownMenuItem>
                )
              })
            )}
          </div>
        )}

        {/* Create a new organization */}
        {hasMultipleTenants && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onClick={handleCreateOrganization}
          className="flex min-h-11 cursor-pointer items-center gap-2"
        >
          <Plus className="w-4 h-4 text-ink-2 shrink-0" />
          <span>Create organization</span>
        </DropdownMenuItem>

        {/* Super-admin indicator (optional) */}
        {user?.is_staff && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-ink-2">
              Super admin access
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
